import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { withApiState } from '@/utils/withApiState'

export type LegalPageStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'

interface LegalPageTranslation {
  uuid: string
  language: string
  title: string
  body: string
}

export interface LegalPageItem {
  uuid: string
  key: string
  status: LegalPageStatus
  order: number
  availableLanguages: string[]
  titleFr: string | null
  publishedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface LegalPageDetail extends LegalPageItem {
  translations: LegalPageTranslation[]
}

interface LegalPageTranslationInput {
  language: string
  title: string
  body: string
}

export interface CreateLegalPagePayload {
  key: string
  status?: LegalPageStatus
  order?: number
  translations: LegalPageTranslationInput[]
}

export type UpdateLegalPagePayload = {
  status?: LegalPageStatus
  order?: number
  translations?: LegalPageTranslationInput[]
}

export interface ListLegalPageQuery {
  status?: LegalPageStatus
}

export interface PublishValidationError {
  code: string
  message: string
  detail?: unknown
}

export interface PublishResult {
  ok: boolean
  errors?: PublishValidationError[]
  page?: LegalPageItem
}

type StoreResult<T> = { ok: true; data: T } | { ok: false; message: string }

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json()
    if (body?.message) return Array.isArray(body.message) ? body.message.join(', ') : String(body.message)
  } catch {
    // ignore
  }
  return fallback
}

function buildQueryString(query: ListLegalPageQuery): string {
  const params = new URLSearchParams()
  if (query.status) params.append('status', query.status)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const useLegalPagesAdminStore = defineStore('legalPagesAdmin', () => {
  const suiteStore = useSuiteStore()
  const items = ref<LegalPageItem[]>([])
  const isLoading = ref(false)

  async function fetchAll(query: ListLegalPageQuery = {}): Promise<LegalPageItem[]> {
    const result = await withApiState(
      async () => {
        const res = await suiteStore.suiteFetch(`client-space/admin/legal-pages${buildQueryString(query)}`, {
          method: 'GET',
          credentials: 'include',
        })
        if (res && typeof res !== 'boolean' && res.ok) {
          items.value = (await res.json()) as LegalPageItem[]
          return items.value
        }
        return undefined
      },
      { loading: isLoading, logLabel: 'Erreur récupération pages légales' },
    )
    return result ?? []
  }

  async function fetchDetail(uuid: string): Promise<LegalPageDetail | null> {
    const res = await suiteStore.suiteFetch(`client-space/admin/legal-pages/${uuid}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) as LegalPageDetail
    }
    return null
  }

  async function create(payload: CreateLegalPagePayload): Promise<StoreResult<LegalPageDetail>> {
    const res = await suiteStore.suiteFetch('client-space/admin/legal-pages', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as LegalPageDetail
      await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la création')
      : 'Erreur lors de la création'
    return { ok: false, message }
  }

  async function update(uuid: string, payload: UpdateLegalPagePayload): Promise<StoreResult<LegalPageDetail>> {
    const res = await suiteStore.suiteFetch(`client-space/admin/legal-pages/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as LegalPageDetail
      await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la mise à jour')
      : 'Erreur lors de la mise à jour'
    return { ok: false, message }
  }

  async function publish(uuid: string): Promise<StoreResult<PublishResult>> {
    const res = await suiteStore.suiteFetch(`client-space/admin/legal-pages/${uuid}/publish`, {
      method: 'POST',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as PublishResult
      if (data.ok) await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la publication')
      : 'Erreur lors de la publication'
    return { ok: false, message }
  }

  async function archive(uuid: string): Promise<StoreResult<LegalPageItem>> {
    const res = await suiteStore.suiteFetch(`client-space/admin/legal-pages/${uuid}/archive`, {
      method: 'POST',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as LegalPageItem
      await fetchAll()
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, "Erreur lors de l'archivage")
      : "Erreur lors de l'archivage"
    return { ok: false, message }
  }

  return {
    items,
    isLoading,
    fetchAll,
    fetchDetail,
    create,
    update,
    publish,
    archive,
  }
})
